import CompanyNotificationEmail from '../../components/ui/CompanyNotificationEmail';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { Building2, Mail, Phone, MapPin, Calendar, Edit2, Briefcase, Shield, Cloud } from 'lucide-react';
import { tenantService } from '../../services';
import type { CompanyProfile } from '../../types';

const CompanyProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompany = async () => {
      setIsLoading(true);
      try {
        const data = await tenantService.getCompanyProfile();
        setCompany(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load company profile');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCompany();
  }, []);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-96">
          <Loading size="lg" />
        </div>
      </MainLayout>
    );
  }

  if (!company) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-96">
          <h2 className="text-xl font-semibold text-text-primary mb-2">Company Profile Not Found</h2>
          <p className="text-text-muted mb-4">Unable to load company profile.</p>
          <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
        </div>
      </MainLayout>
    );
  }

  // Build address string
  const addressParts = [
    company.address_line_1,
    company.address_line_2,
    company.city_name,
    company.state_name,
    company.country_name,
    company.postal_code,
  ].filter(Boolean);
  const fullAddress = addressParts.join(', ') || 'No address provided';

  return (
    <MainLayout>
      <div className="page-stack">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Company Profile</h1>
            <p className="page-description">View and manage your company information</p>
          </div>
          <Button onClick={() => navigate('/company/edit')}>
            <Edit2 size={18} className="mr-2" />
            Edit Profile
          </Button>
        </div>

        {error && (
          <Alert variant="error" message={error} onClose={() => setError(null)} />
        )}

        {/* Main Profile Card */}
        <Card>
          <div className="p-6">
            <div className="flex flex-col md:flex-row items-start gap-6 mb-8">
              {/* Logo/Avatar */}
              <div className="w-24 h-24 bg-accent-primary/10 rounded-2xl flex items-center justify-center shrink-0">
                {company.logo_url ? (
                  <img 
                    src={company.logo_url} 
                    alt={company.company_name} 
                    className="w-full h-full object-cover rounded-2xl"
                  />
                ) : (
                  <Building2 size={48} className="text-accent-primary" />
                )}
              </div>

              {/* Company Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-text-primary">{company.company_name}</h2>
                  <Badge variant={company.status ? 'success' : 'danger'}>
                    {company.status ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <p className="text-text-muted mb-4">
                  Company Code: <span className="font-mono">{company.company_code || 'N/A'}</span>
                </p>

                <div className="flex flex-wrap gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Mail size={16} className="text-text-muted" />
                    <span className="text-text-primary">{company.email || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-text-muted" />
                    <span className="text-text-primary">{company.phone || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Address Section */}
              <div className="bg-surface-secondary rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={18} className="text-accent-primary" />
                  <h3 className="font-semibold text-text-primary">Address</h3>
                </div>
                <p className="text-text-secondary text-sm">{fullAddress}</p>
              </div>

              {/* Business Type Section */}
              <div className="bg-surface-secondary rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase size={18} className="text-accent-primary" />
                  <h3 className="font-semibold text-text-primary">Business Type</h3>
                </div>
                <p className="text-text-secondary text-sm">{company.business_type_name || 'Not specified'}</p>
                {company.sub_business_name && (
                  <p className="text-text-muted text-xs mt-1">{company.sub_business_name}</p>
                )}
              </div>

              {/* Service Provider Section */}
              <div className="bg-surface-secondary rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 size={18} className="text-accent-primary" />
                  <h3 className="font-semibold text-text-primary">Service Provider</h3>
                </div>
                <p className="text-text-secondary text-sm">
                  {company.parent_company_name || 'None (Top-level tenant)'}
                </p>
              </div>

              {/* Valid Until Section */}
              <div className="bg-surface-secondary rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar size={18} className="text-accent-primary" />
                  <h3 className="font-semibold text-text-primary">Valid Until</h3>
                </div>
                <p className="text-text-secondary text-sm">
                  {company.valid_until 
                    ? new Date(company.valid_until).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'No expiration'}
                </p>
              </div>

              {/* Status Section */}
              <div className="bg-surface-secondary rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Shield size={18} className="text-accent-primary" />
                  <h3 className="font-semibold text-text-primary">Account Status</h3>
                </div>
                <Badge variant={company.status ? 'success' : 'danger'} className="mt-1">
                  {company.status ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="hover:border-accent-primary/50 transition-colors cursor-pointer" onClick={() => navigate('/tenants')}>
            <div className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-accent-primary/10 rounded-xl flex items-center justify-center">
                <Building2 size={24} className="text-accent-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-text-primary">Manage Tenants</h3>
                <p className="text-sm text-text-muted">View and manage your sub-tenants</p>
              </div>
            </div>
          </Card>

          <Card className="hover:border-accent-primary/50 transition-colors cursor-pointer" onClick={() => navigate('/tenants/create')}>
            <div className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-status-success/10 rounded-xl flex items-center justify-center">
                <Building2 size={24} className="text-status-success" />
              </div>
              <div>
                <h3 className="font-semibold text-text-primary">Add New Tenant</h3>
                <p className="text-sm text-text-muted">Create a new sub-tenant company</p>
              </div>
            </div>
          </Card>

          <Card className="hover:border-accent-primary/50 transition-colors cursor-pointer" onClick={() => navigate('/integrations')}>
            <div className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                <Cloud size={24} className="text-blue-500" />
              </div>
              <div>
                <h3 className="font-semibold text-text-primary">Cloud Integrations</h3>
                <p className="text-sm text-text-muted">Configure event storage and face matching</p>
              </div>
            </div>
          </Card>
        </div>
        <CompanyNotificationEmail />
      </div>
    </MainLayout>
  );
};

export default CompanyProfilePage;
